import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Image,
  SkeletonText,
  Text,
  VStack,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import React from "react";
import { MdPlayCircle } from "react-icons/md";
import { RiCalendarTodoFill, RiTimerLine } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { openAddVoucherModel } from "../../../modules/admin-modules/models/Actions";
import { moreVideoViewsRequest } from "../../../modules/students-modules/videos/Actions";
import { countSeens } from "./func";

const C = {
  primary: "#E85D04",
  secondary: "#9A3412",
  border: "#E8E0D5",
  cream: "#FFF7ED",
  paper: "#FFFFFF",
  mutedBg: "#F5F0E8",
  muted: "#78716C",
};

const BASE_URL = "https://api.asquera.com/public/thumbnails";

function videoThumbUrl(data) {
  const path = data?.thumbnail;
  if (!path || path === "null" || path === "NULL" || path === "undefined") return null;
  if (String(path).startsWith("http")) return path;
  return `${BASE_URL}/${String(path).replace(/^\//, "")}`;
}

function videoAlertMessage(data) {
  const vc = data?.views_count?.views_count;
  if (vc === 3) return "Please contact technical support";
  if (vc === 4) {
    return "You have exceeded the allowed watch duration. You can request a voucher and contact your assistant.";
  }
  if (data?.suspended === 1 && data?.user_video_suspended !== 0) {
    if (data?.related_to_homework === 1 && data?.exams?.length) {
      return `Video Locked please submit hw or solve the exam ${data.exams[0]?.name}`;
    }
    if (data?.related_to_homework === 1 && !data?.exams?.length) {
      return "Video Locked please submit hw";
    }
    if (data?.related_to_homework === 0 && data?.exams?.length) {
      return `Video Locked please solve the exam ${data.exams[0]?.name}`;
    }
    return "Video Locked request voucher and contact the assistant";
  }
  return "";
}

const VideoCard = ({ data, index, selectedindex, handelClick, isSelected }) => {
  const date1 = dayjs(data?.expiry_date);
  const diff = date1.diff(new Date(), "day");
  const seenLoading = useSelector((state) => state.chapterVideos.seenLoading);
  const voucherLoading = useSelector((state) => state.chapterVideos.voucherLoading);
  const userData = useSelector((state) => state.user.userData);
  const dispatch = useDispatch();

  const thumb = videoThumbUrl(data);
  const alert = videoAlertMessage(data);
  const alertColor = data?.views_count?.views_count >= 4 ? "#b83232" : `${C.primary}cc`;
  const showVoucherRow =
    data?.max_allowed_views <= data?.views_count?.views_count ||
    (data?.suspended == 1 && data?.user_video_suspended != 0);
  const showWatchButton = !alert && !showVoucherRow;

  const handleWatch = (e) => {
    e?.stopPropagation?.();
    if (data?.suspended != 1) {
      countSeens(dispatch, data);
    }
    handelClick && handelClick();
  };

  return (
    <Box
      bg={isSelected ? C.mutedBg : C.paper}
      borderWidth="1px"
      borderColor={isSelected ? C.primary : C.border}
      borderRadius="xl"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      h="100%"
      cursor="pointer"
      transition="transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease"
      willChange="transform"
      _hover={{
        borderColor: `${C.primary}66`,
        transform: "scale(1.02)",
        boxShadow: "0 18px 46px rgba(232, 93, 4, 0.18), 0 0 0 1px rgba(232, 224, 213, 0.35)",
      }}
      _active={{ transform: "scale(1.01)" }}
      onClick={handleWatch}
    >
      <Box
        position="relative"
        h={{ base: "132px", sm: "144px" }}
        overflow="hidden"
        bgGradient="linear(to-br, rgba(232, 93, 4, 0.12), rgba(232, 93, 4, 0.06))"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Box
          position="absolute"
          top={3}
          left={3}
          bg={C.primary}
          color="white"
          fontSize="9px"
          fontWeight="800"
          textTransform="uppercase"
          letterSpacing="0.06em"
          px={2}
          py={0.5}
          borderRadius="full"
          zIndex={2}
        >
          Session
        </Box>
        {thumb ? (
          <>
            <Image
              src={thumb}
              alt={`${data?.title || "Video"} thumbnail`}
              position="absolute"
              inset={0}
              w="100%"
              h="100%"
              objectFit="cover"
              objectPosition="center"
              display="block"
            />
            <Box
              position="absolute"
              inset={0}
              bgGradient="linear(to-br, rgba(232, 93, 4, 0.25), rgba(154, 52, 18, 0.14))"
            />
            <Box position="relative" zIndex={1}>
              <MdPlayCircle color="white" size={52} />
            </Box>
          </>
        ) : (
          <MdPlayCircle color={C.primary} size={48} />
        )}
      </Box>

      <Box
        p={4}
        borderTopWidth="1px"
        borderTopColor="rgba(232, 224, 213, 0.75)"
        display="flex"
        flexDirection="column"
        flex="1"
        minH="168px"
      >
        <Text fontSize="sm" fontWeight="800" color={C.primary} noOfLines={2} mb={1}>
          {data?.title}
        </Text>

        {seenLoading && index == selectedindex ? (
          <SkeletonText startColor="gray.100" endColor="gray.300" w="100%" noOfLines={2} spacing="3" />
        ) : (
          <>
            <VStack align="stretch" spacing={1} mb={2}>
              <HStack spacing={1.5} color={C.muted}>
                <Icon as={RiCalendarTodoFill} fontSize="11px" />
                <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em">
                  {dayjs(data?.created_at).format("D MMM, YYYY")}
                </Text>
              </HStack>
              {diff !== null && !Number.isNaN(diff) && userData?.subject?.id != "34" && (
                <HStack spacing={1.5} color={C.muted}>
                  <Icon as={RiTimerLine} fontSize="11px" />
                  <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em">
                    {diff > 0 ? diff : 0} Days remaining
                  </Text>
                </HStack>
              )}
            </VStack>

            {alert && userData?.subject?.id != "34" && data?.max_allowed_views ? (
              <Text fontSize="xs" fontWeight="600" color={alertColor} mb={3} lineHeight="1.45">
                {alert}
              </Text>
            ) : null}
          </>
        )}

        <Box mt="auto">
          {showVoucherRow ? (
            <Flex direction={{ base: "column", sm: "row" }} gap={2} mb={showWatchButton ? 2 : 0}>
              <Button
                flex={1}
                size="sm"
                bg={C.primary}
                color="white"
                fontWeight="800"
                textTransform="uppercase"
                fontSize="10px"
                borderRadius="lg"
                _hover={{ bg: C.secondary }}
                onClick={(e) => {
                  e.stopPropagation();
                  let formData = new FormData();
                  formData.append("video_id", data?.id);
                  dispatch(moreVideoViewsRequest(formData));
                }}
              >
                Request Voucher
              </Button>
              <Button
                flex={1}
                size="sm"
                variant="outline"
                borderWidth="2px"
                borderColor={C.primary}
                color={C.primary}
                fontWeight="800"
                textTransform="uppercase"
                fontSize="10px"
                borderRadius="lg"
                isLoading={voucherLoading}
                _hover={{ bg: C.secondary, color: C.cream, borderColor: C.secondary }}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(openAddVoucherModel(data?.id));
                }}
              >
                Add Voucher
              </Button>
            </Flex>
          ) : null}

          {showWatchButton ? (
            <Button
              w="full"
              size="sm"
              variant="outline"
              borderWidth="2px"
              borderColor={C.primary}
              color={C.primary}
              fontWeight="800"
              textTransform="uppercase"
              fontSize="10px"
              borderRadius="lg"
              _hover={{ bg: `${C.primary}12` }}
              onClick={handleWatch}
            >
              Watch
            </Button>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default VideoCard;
